/**
 * Lung Cancer Screening (LDCT) eligibility logic.
 * Shared by LungScreeningForm.js (form display) and LungScreeningPDF.js
 * (PDF output) so the two never drift — same pattern as the TI-RADS
 * helpers in pdfHelpers.js.
 *
 * Criteria per CMS NCD 210.14 (effective 2022-02-10, confirmed current as
 * of 2026-07): age 50-77, asymptomatic, >=20 pack-year smoking history,
 * currently smoking or quit within the last 15 years.
 */

/**
 * Parse a "YYYY-MM-DD" (or "YYYY-MM-DDTxx:xx" ) string into integer parts
 * without going through a Date object — avoids the UTC-midnight timezone
 * rollback that bit formatExamDate() before it was fixed (see pdfHelpers.js).
 */
function parseISODateParts(dateString) {
  if (!dateString) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
  if (!m) return null;
  return { year: parseInt(m[1], 10), month: parseInt(m[2], 10), day: parseInt(m[3], 10) };
}

/**
 * Age in whole years as of referenceDate (defaults to today), computed via
 * pure integer arithmetic on date parts — no timezone-sensitive Date math.
 */
export function calculateAge(dateOfBirth, referenceDate) {
  const dob = parseISODateParts(dateOfBirth);
  if (!dob) return null;

  const refStr = referenceDate || new Date().toISOString().split('T')[0];
  const ref = parseISODateParts(refStr) || parseISODateParts(new Date().toISOString().split('T')[0]);

  let age = ref.year - dob.year;
  if (ref.month < dob.month || (ref.month === dob.month && ref.day < dob.day)) {
    age--;
  }
  return age;
}

export const SYMPTOM_OPTIONS = [
  'Persistent or worsening cough',
  'Hemoptysis (coughing up blood)',
  'Unexplained weight loss',
  'Chest pain',
  'Shortness of breath / dyspnea',
  'Recurrent pneumonia or bronchitis'
];

export const SDM_CHECKLIST_ITEMS = [
  { key: 'confirmedHighRiskCriteria', label: 'Confirmed patient meets high-risk criteria' },
  { key: 'discussedBenefitsRisks', label: 'Discussed benefits and risks of screening' },
  { key: 'discussedFollowUp', label: 'Provided information on follow-up after the scan' },
  { key: 'discussedOverdiagnosisRisks', label: 'Discussed risks of over-diagnosis, radiation exposure, and false positives' },
  { key: 'counseledAnnualScreening', label: 'Counseled on importance of annual screening and other causes of death' },
  { key: 'counseledSmokingCessation', label: 'Counseled on importance of smoking cessation, including Medicare-covered services' }
];

/**
 * Evaluate CMS LDCT lung cancer screening eligibility from form data.
 * Returns { eligible, reasons } — reasons is empty when eligible, and lists
 * every failing/missing criterion (not just the first) when not, so the
 * technologist/nurse can see the full picture at once.
 */
export function getLungScreeningEligibility(data) {
  const reasons = [];
  const age = calculateAge(data.dateOfBirth, data.examDate);

  if (age === null) {
    reasons.push('Date of birth not entered');
  } else if (age < 50 || age > 77) {
    reasons.push(`Age ${age} is outside the covered range (50-77 years)`);
  }

  if (!data.smokingStatus) {
    reasons.push('Smoking status not specified');
  } else if (data.smokingStatus === 'Never smoked') {
    reasons.push('No smoking history reported (screening requires a smoking history)');
  } else if (data.smokingStatus === 'Former smoker') {
    const yearsSinceQuit = parseFloat(data.yearsSinceQuit);
    if (data.yearsSinceQuit === '' || data.yearsSinceQuit === undefined || isNaN(yearsSinceQuit)) {
      reasons.push('Years since quitting smoking not entered');
    } else if (yearsSinceQuit > 15) {
      reasons.push(`Quit smoking ${yearsSinceQuit} years ago — more than the 15-year covered window`);
    }
  }

  const packYears = parseFloat(data.packYears);
  if (data.packYears === '' || data.packYears === undefined || isNaN(packYears)) {
    reasons.push('Smoking history (pack-years) not entered');
  } else if (packYears < 20) {
    reasons.push(`Smoking history of ${packYears} pack-years is below the 20 pack-year minimum`);
  }

  const symptomsPresent = Array.isArray(data.symptoms) && data.symptoms.length > 0;
  if (symptomsPresent) {
    reasons.push(`Current signs/symptoms of lung cancer reported: ${data.symptoms.join(', ')}`);
  }

  return {
    eligible: reasons.length === 0,
    reasons,
    age
  };
}
